import {BasicActivityTypesEnum} from '../../../../../models/api/activityModel';
import {RuleBundleModelStrict, RuleBundlePrioritiesEnum} from '../../../../../models/api/ruleBundleModel';
import {RuleBundleTypesEnum} from '../../../../../models/api/ruleCommonTypes';
import {
  RuleBaseModelStrict,
  RuleCompoundConditions,
  RuleCompoundConditionTypesEnum,
  SimpleCompoundConditions,
} from '../../../../../models/api/ruleModel';
import {DelayParameter} from '../../../../editors/commons/paramModel';
import {CsatRuleRolesEnum} from '../../../csatRuleHelpers';
import {buildRuleAction} from '../../../ruleActionHelpers';
import {RuleAction, RuleActionTypesEnum} from '../../../ruleActionModel';
import {buildRuleCondition} from '../../../ruleConditionHelpers';
import {RuleConditionTypesEnum} from '../../../ruleConditionModel';
import {RuleEventTypesEnum} from '../../../ruleEventModel';
import {RuleTriggerEventTypes} from '../../../ruleTriggerModel';
import {CsatEditorState} from './state/csatEditorState';

export function buildCsatBundleFromState(state: CsatEditorState, namespace: string): RuleBundleModelStrict {
  const recipeName = state.ruleState.name;

  const sentTag = state.csatState.tags.sent;
  const scheduledTag = state.csatState.tags.scheduled;
  const csatResponse = state.csatState.response;

  if (!csatResponse) {
    throw new Error('CSAT rule does not include reply template');
  }

  const csatDelay = state.csatState.delaySinceTrigger.delay;

  if (!csatDelay) {
    throw new Error('CSAT rule does not include a delay');
  }

  // Sent Rule.
  const sentRule: RuleBaseModelStrict = {
    id: state.csatState.initialRules?.sentRule.id,
    name: `${recipeName} - CSAT: Sent`,
    namespace,
    events: csatSurveyTriggerEvents,
    expressions: [],
    compoundConditions: sentCompoundConditions(state, sentTag, scheduledTag, csatDelay),
    actions: sentRuleActions(state, sentTag, scheduledTag, csatResponse),
    active: state.ruleState.isActive,
    stopProcessing: false,
    sortOrder: 1,
    role: CsatRuleRolesEnum.SURVEY_SENT,
    description: '',
    footprintNamespaces: [namespace],
    lastTriggeredAt: undefined,
  };

  // Scheduled Rule.
  const scheduledRule: RuleBaseModelStrict = {
    id: state.csatState.initialRules?.scheduledRule.id,
    name: `${recipeName} - CSAT: Survey Scheduled`,
    namespace,
    events: csatSurveyTriggerEvents,
    expressions: [],
    compoundConditions: scheduledCompoundConditions(state, sentTag),
    actions: scheduledRuleActions(state, scheduledTag),
    active: state.ruleState.isActive,
    stopProcessing: false,
    sortOrder: 4,
    role: CsatRuleRolesEnum.SURVEY_SCHEDULED,
    description: '',
    footprintNamespaces: [namespace],
    lastTriggeredAt: undefined,
  };

  // Unschedule Rule.
  const unscheduleRule: RuleBaseModelStrict = {
    id: state.csatState.initialRules?.unscheduleRule?.id,
    name: `${recipeName} - CSAT: Unschedule Survey`,
    namespace,
    events: csatUnscheduleTriggerEvents,
    expressions: [],
    compoundConditions: unscheduleCompoundConditions(scheduledTag),
    actions: unscheduleRuleActions(scheduledTag),
    active: state.ruleState.isActive,
    stopProcessing: false,
    sortOrder: 4,
    role: CsatRuleRolesEnum.REMOVE_SCHEDULED_ON_INBOUND,
    description: '',
    footprintNamespaces: [namespace],
    lastTriggeredAt: undefined,
  };

  const maybeBundle = state.csatState.initialRules?.bundle;

  return {
    id: maybeBundle?.id,
    name: recipeName,
    type: RuleBundleTypesEnum.CSAT,
    namespace: maybeBundle?.namespace ?? namespace,
    priority: maybeBundle?.priority ?? RuleBundlePrioritiesEnum.DEFAULT,
    rules: [sentRule, scheduledRule, unscheduleRule],
    sortOrder: maybeBundle?.sortOrder,
    url: undefined,
  };
}

/*
 * Constants.
 */

const csatSurveyTriggerEvents: ReadonlyArray<RuleTriggerEventTypes> = [BasicActivityTypesEnum.ARCHIVE];

const csatUnscheduleTriggerEvents: ReadonlyArray<RuleTriggerEventTypes> = [
  RuleEventTypesEnum.INBOUND,
  RuleEventTypesEnum.REOPEN,
  BasicActivityTypesEnum.REMINDER,
];

/*
 * Helpers.
 */

function sentCompoundConditions(
  csatState: CsatEditorState,
  sentTag: number,
  scheduledTag: number,
  delay: DelayParameter,
): RuleCompoundConditions {
  const orConditions: ReadonlyArray<SimpleCompoundConditions> = csatState.ruleState.conditions.map(
    (orCond) => [RuleCompoundConditionTypesEnum.OR, ...orCond],
  );

  // Implicit sent conditions that are not directly editable by the user.
  const fixedConditions = [
    buildRuleCondition(RuleConditionTypesEnum.HASNT_TAG, {tag: sentTag}),
    buildRuleCondition(RuleConditionTypesEnum.HAS_TAG, {tag: scheduledTag}),
    buildRuleCondition(RuleConditionTypesEnum.DELAY_SINCE_TRIGGER, {delay}),
  ];

  return [RuleCompoundConditionTypesEnum.AND, ...fixedConditions, ...orConditions];
}

function scheduledCompoundConditions(csatState: CsatEditorState, tagId: number): RuleCompoundConditions {
  const orConditions: ReadonlyArray<SimpleCompoundConditions> = csatState.ruleState.conditions.map(
    (orCond) => [RuleCompoundConditionTypesEnum.OR, ...orCond],
  );

  // Implicit scheduled conditions that are not directly editable by the user.
  const fixedConditions = [buildRuleCondition(RuleConditionTypesEnum.HASNT_TAG, {tag: tagId})];

  return [RuleCompoundConditionTypesEnum.AND, ...fixedConditions, ...orConditions];
}

function unscheduleCompoundConditions(scheduledTag: number): RuleCompoundConditions {
  return [
    RuleCompoundConditionTypesEnum.AND,
    [
      RuleCompoundConditionTypesEnum.OR,
      buildRuleCondition(RuleConditionTypesEnum.HAS_TAG, {tag: scheduledTag}),
    ],
    [RuleCompoundConditionTypesEnum.OR, buildRuleCondition(RuleConditionTypesEnum.IS_OPEN, {})],
  ];
}

function sentRuleActions(
  csatState: CsatEditorState,
  sentTag: number,
  scheduledTag: number,
  csatResponse: number,
): ReadonlyArray<RuleAction> {
  return [
    buildRuleAction(RuleActionTypesEnum.TAG, {tag: sentTag}),
    buildRuleAction(RuleActionTypesEnum.UNTAG, {tag: scheduledTag}),
    buildRuleAction(RuleActionTypesEnum.REPLY, {when: 'always', response: csatResponse}),
    ...csatState.csatState.actions.sent,
  ];
}

function scheduledRuleActions(csatState: CsatEditorState, tag: number): ReadonlyArray<RuleAction> {
  return [buildRuleAction(RuleActionTypesEnum.TAG, {tag}), ...csatState.csatState.actions.scheduled];
}

function unscheduleRuleActions(scheduledTag: number): ReadonlyArray<RuleAction> {
  return [buildRuleAction(RuleActionTypesEnum.UNTAG, {tag: scheduledTag})];
}
